import React from "react";

import Highcharts, { Options } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useQuery } from "react-query";
import { Card, Typography } from "@material-ui/core";

import { MtuTable } from "./MtuTable";

import { makeRequest } from "../../util";
import {
  MtuUsageByMonthData,
  MtuUsageByEnvData,
  MtuUsageData,
  DataSeries,
} from "../types";

const useFetchMtu = () => {
  return useQuery("mtuUsage", async () => {
    const { data: res } = await makeRequest<MtuUsageData>("get_mtu");
    return res;
  });
};

Highcharts.setOptions({
  lang: {
    decimalPoint: ".",
    thousandsSep: ",",
  },
});

const MtuUsage = () => {
  const { data, isLoading } = useFetchMtu();

  const mtuByMonth: MtuUsageByMonthData[] = data?.byMonth ?? [];
  const mtuUsageSeries = mtuByMonth.map((d) => d.mtuUsage) ?? [];
  const contractedMtuSeries = mtuByMonth.map((d) => d.contractedMtu) ?? [];
  const mtuLabels = mtuByMonth.map((d) => d.yearMonth) ?? [];

  const colChartOptions: Options = {
    chart: {
      type: "column",
    },
    credits: {
      enabled: false,
    },
    accessibility: {
      enabled: false,
    },
    title: {
      text: "",
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
        },
      },
      spline: {
        dataLabels: {
          enabled: true,
        },
      },
    },
    series: [
      {
        name: "MTU Usage",
        type: "column",
        data: mtuUsageSeries,
      },
      {
        name: "Contracted MTU",
        type: "spline",
        data: contractedMtuSeries,
      },
    ],
    xAxis: [
      {
        categories: mtuLabels,
        crosshair: true,
      },
    ],
    yAxis: {
      labels: {
        enabled: true,
      },
      title: {
        text: "",
      },
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const overage =
          ((this.points ?? [])[0]?.y ?? 0) / ((this.points ?? [])[1]?.y ?? 1);
        const overagePercentage = overage * 100;
        if (overage > 1) {
          return `<strong>Overage: </strong>${(overagePercentage - 100).toFixed(
            2
          )}%`;
        }
        return `<strong>Usage: </strong>${overagePercentage.toFixed(2)}%`;
      },
    },
  };

  const mtuByEnv: MtuUsageByEnvData[] = data?.byEnv ?? [];
  const mtuYearMonth = mtuByEnv.reduce((acc: string[], cur): string[] => {
    if (!acc.includes(cur.yearMonth)) {
      acc.push(cur.yearMonth);
    }
    return acc;
  }, []);

  const projEnv = mtuByEnv.reduce((acc: string[], cur): string[] => {
    if (!acc.includes(cur.projectName + " : " + cur.envName)) {
      acc.push(cur.projectName + " : " + cur.envName);
    }
    return acc;
  }, []);

  const dataSeriesFunction = (
    mtuYearMonth?: string[],
    projEnv?: string[],
    data?: MtuUsageByEnvData[]
  ): DataSeries[] => {
    if (!mtuYearMonth || !projEnv || !data) return [];

    const dat: { [key: string]: number[] } = {};
    projEnv.forEach((pe) => {
      dat[pe] = new Array(mtuYearMonth.length).fill(0);
    });
    data.forEach((d) => {
      dat[d.projectName + " : " + d.envName][
        mtuYearMonth.indexOf(d.yearMonth)
      ] = d.mtuUsage;
    });

    const final: DataSeries[] = [];
    Object.keys(dat).forEach((k) => {
      final.push({
        type: "column",
        name: k,
        data: dat[k],
      });
    });
    return final;
  };

  const dataSeries = dataSeriesFunction(mtuYearMonth, projEnv, mtuByEnv);

  const stackChartOptions: Options = {
    chart: {
      type: "column",
    },
    credits: {
      enabled: false,
    },
    accessibility: {
      enabled: false,
    },
    title: {
      text: "",
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: {
          enabled: false,
        },
      },
    },
    series: dataSeries as Highcharts.SeriesOptionsType[],
    xAxis: [
      {
        categories: mtuYearMonth,
        crosshair: true,
      },
    ],
    yAxis: {
      labels: {
        enabled: true,
      },
      title: {
        text: "",
      },
    },
  };
  return isLoading ? (
    <div>Loading...</div>
  ) : (
    <>
      <div className="h-full w-full">
        <Typography variant="h4">MTU Usage</Typography>
        <Card className="mt-2">
          <HighchartsReact highcharts={Highcharts} options={colChartOptions} />
        </Card>
        <MtuTable data={mtuByMonth} />
      </div>
      <div className="h-full w-full">
        <Typography variant="h4">
          MTU Usage By Project By Environment
        </Typography>
        <Card className="mt-2">
          <HighchartsReact
            highcharts={Highcharts}
            options={stackChartOptions}
          />
        </Card>
      </div>
    </>
  );
};

export default MtuUsage;
